import { useQuery } from '@tanstack/react-query';
import {
  FILTROS_DE_ANUNCIO,
  JANELAS_DE_DESEMPENHO,
  JANELA_PADRAO,
  MOTIVO_FORA_DO_AR,
  ROTULO_ESTADO_ANUNCIO,
  ROTULO_FILTRO_DE_ANUNCIO,
  ROTULO_JANELA_DE_DESEMPENHO,
  type EstadoAnuncio,
  type FiltroDeAnuncio,
  type JanelaDeDesempenho,
} from '@reducee/contracts';
import { useState } from 'react';

import { chamarApi } from '../api/cliente.ts';
import { CampoSelecao } from './Campo.tsx';
import { BotaoDePagina, Cartao, Pilula, type Tom } from './Superficie.tsx';

type AnuncioNaParede = {
  id: string;
  nome: string;
  campanhaDeAnunciosNome: string | null;
  conjuntoDeAnunciosNome: string | null;
  estado: EstadoAnuncio;
  imagemUrl: string | null;
  sincronizadoEm: string;
  leads: number | null;
  gasto: number | null;
  custoPorLead: number | null;
};

type Parede = {
  anuncios: AnuncioNaParede[];
  total: number;
  existem: number;
  pagina: number;
  porPagina: number;
  resumo: {
    anuncios: number;
    noAr: number;
    foraDoAr: number;
    comLead: number;
    leadsDeAnuncio: number;
    porEstado: Array<{ estado: EstadoAnuncio; quantidade: number }>;
  };
  janela: JanelaDeDesempenho;
  espelhadoEm: string | null;
};

/**
 * Valor em real, com vírgula decimal.
 *
 * Tracinho quando não há número, e nunca `R$ 0,00`: zero afirma que a peça não
 * custou nada, e o que se sabe é que a Meta não reportou gasto dela na janela.
 */
function reais(valor: number | null): string {
  if (valor === null) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * A cor diz o que fazer com o Anúncio, e não o que ele é.
 *
 * No ar é o único verde. Reprovado e com problema são vermelhos porque pedem
 * conserto no Gerenciador, e estado não reconhecido é alerta justamente por
 * não afirmar nada: a Meta respondeu algo que esta versão não sabe ler.
 */
const TOM_DO_ESTADO: Record<EstadoAnuncio, Tom> = {
  NO_AR: 'sucesso',
  PAUSADO: 'neutro',
  CAMPANHA_PAUSADA: 'neutro',
  CONJUNTO_PAUSADO: 'neutro',
  REPROVADO: 'erro',
  COM_PROBLEMA: 'erro',
  EM_ANALISE: 'informacao',
  ARQUIVADO: 'neutro',
  DESCONHECIDO: 'alerta',
};

/**
 * Parede de Anúncios da Meta, aberta dentro de Configurações.
 *
 * Mostra a cópia que o worker espelha de hora em hora — a Reducee nunca cria
 * nem edita anúncio. Não vai à Meta: com a Meta fora do ar a parede continua
 * abrindo, e o que envelhece é a data de espelhamento no topo.
 *
 * Abre em "No ar" porque é o que a pergunta "o que está rodando?" quer, e
 * quem filtra é a API: o padrão vive no esquema Zod, não neste `useState`.
 *
 * Quem monta este componente é o botão de Configurações, e só depois do
 * clique: sem isso, toda abertura da tela de parâmetros pagaria a consulta da
 * parede e o download das imagens para quem foi ali mexer noutra coisa.
 */
export function ParedeDeAnuncios(): React.JSX.Element {
  const [filtro, setFiltro] = useState<FiltroDeAnuncio>('NO_AR');
  const [pagina, setPagina] = useState(1);
  const [janela, setJanela] = useState<JanelaDeDesempenho>(JANELA_PADRAO);

  const consulta = useQuery({
    queryKey: ['anuncios', filtro, pagina, janela],
    queryFn: () =>
      chamarApi<Parede>(
        `/configuracoes/anuncios?filtro=${filtro}&pagina=${pagina}&janela=${janela}`,
      ),
  });

  const parede = consulta.data;
  const anuncios = parede?.anuncios ?? [];
  const paginas = parede ? Math.max(1, Math.ceil(parede.total / parede.porPagina)) : 1;

  // Trocar de filtro com a página 4 na mão pediria a página 4 de uma lista que
  // pode ter uma só, e a parede abriria vazia sem nada estar errado.
  function escolherFiltro(candidato: FiltroDeAnuncio): void {
    setFiltro(candidato);
    setPagina(1);
  }

  return (
    <div className="mt-4 border-t border-linha pt-4" data-teste="parede-em-configuracoes">
      <p className="mb-3 text-sm text-texto-suave">
        Cópia do Gerenciador, espelhada de hora em hora. Criar, pausar e editar continua sendo lá.
      </p>

      {parede ? <Numeros parede={parede} /> : null}

      <div className="mb-4 flex flex-wrap gap-2" data-teste="filtros-da-parede">
        {FILTROS_DE_ANUNCIO.map((candidato) => (
          <button
            key={candidato}
            type="button"
            onClick={() => escolherFiltro(candidato)}
            aria-pressed={filtro === candidato}
            // `min-h-11` são os 44px de alvo de toque: no celular estes botões
            // e os de página são a única coisa clicável da parede.
            className={`min-h-11 rounded-full border px-4 text-xs font-[850] transition ${
              filtro === candidato
                ? 'border-marinho bg-marinho-suave text-marinho'
                : 'border-linha text-texto-suave'
            }`}
          >
            {ROTULO_FILTRO_DE_ANUNCIO[candidato]}
          </button>
        ))}

        {/* O período muda gasto e custo por Lead, e não a lista: trocá-lo não
            mexe na página, porque os mesmos anúncios continuam ali. */}
        <CampoSelecao
          rotulo="Período"
          className="ml-auto w-40"
          value={janela}
          onChange={(evento) => setJanela(evento.target.value as JanelaDeDesempenho)}
          data-teste="janela-do-desempenho"
        >
          {JANELAS_DE_DESEMPENHO.map((candidata) => (
            <option key={candidata} value={candidata}>
              {ROTULO_JANELA_DE_DESEMPENHO[candidata]}
            </option>
          ))}
        </CampoSelecao>
      </div>

      {consulta.isPending ? <p className="text-texto-suave">Carregando os anúncios…</p> : null}

      {/* Falha tem de aparecer como falha. Tratar erro igual a lista vazia diria
          "não há anúncio no ar" quando a verdade é "não deu para perguntar". */}
      {consulta.isError ? (
        <Cartao>
          <p className="font-[750] text-erro">Não deu para carregar os anúncios.</p>
          <p className="mt-1 text-sm text-texto-suave">
            A cópia continua no banco; foi a consulta que falhou. Tente de novo em instantes.
          </p>
        </Cartao>
      ) : null}

      {parede && anuncios.length === 0 ? (
        <Cartao>
          {/* Esperar só faz sentido quando não há o que esperar chegar. Com a
            * cópia em dia, mandar aguardar uma hora faz procurar defeito onde
            * só há conta sem anúncio ativo — e a varredura já terminou. */}
          <p className="text-texto-suave">{mensagemDeVazio(filtro, parede)}</p>
        </Cartao>
      ) : null}

      <div
        className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4"
        data-teste="parede-de-anuncios"
      >
        {anuncios.map((anuncio) => (
          <CartaoDeAnuncio key={anuncio.id} anuncio={anuncio} />
        ))}
      </div>

      {parede && parede.total > parede.porPagina ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-texto-suave" data-teste="posicao-da-parede">
            Página {parede.pagina} de {paginas} · {parede.total} anúncios
            {/* Só aparece quando a conta passa do teto da listagem: dizer "112
                de 112" seria ruído, e omitir num filtro de mil seria mentira. */}
            {parede.existem > parede.total ? ` (de ${parede.existem} no filtro)` : ''}
          </p>
          <div className="flex gap-2">
            <BotaoDePagina
              rotulo="Anterior"
              desabilitado={parede.pagina <= 1}
              aoClicar={() => setPagina((atual) => Math.max(1, atual - 1))}
            />
            <BotaoDePagina
              rotulo="Próxima"
              desabilitado={parede.pagina >= paginas}
              aoClicar={() => setPagina((atual) => Math.min(paginas, atual + 1))}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Os números da conta, e não os do filtro.
 *
 * São a resposta que a parede sozinha não dá: 2 de 114 no ar é a informação,
 * e ela desaparece se a tela só souber contar o que está mostrando.
 */
function Numeros({ parede }: { parede: Parede }): React.JSX.Element {
  const { resumo, espelhadoEm } = parede;

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-teste="numeros-dos-anuncios">
      {/* Rótulo curto porque em 390px são duas colunas: "Anúncios espelhados"
          truncava no meio, e o detalhe abaixo de cada número já diz o resto. */}
      <Numero rotulo="Espelhados" valor={String(resumo.anuncios)} />
      <Numero rotulo="No ar" valor={String(resumo.noAr)} tom="sucesso" />
      <Numero rotulo="Fora do ar" valor={String(resumo.foraDoAr)} />
      <Numero
        rotulo="Leads"
        // Tracinho, nunca zero: a origem do Lead só passou a ser gravada agora,
        // e "0" leria como "anúncio nenhum rendeu".
        valor={resumo.leadsDeAnuncio > 0 ? String(resumo.leadsDeAnuncio) : '—'}
        detalhe={
          resumo.leadsDeAnuncio > 0
            ? `de anúncio, em ${resumo.comLead} ${resumo.comLead === 1 ? 'peça' : 'peças'}`
            : 'origem ainda não registrada'
        }
      />
      <p className="col-span-2 text-xs text-texto-suave md:col-span-4">
        {espelhadoEm
          ? `Espelhado da Meta em ${new Date(espelhadoEm).toLocaleString('pt-BR')}.`
          : 'Nenhuma varredura concluída ainda.'}{' '}
        {resumo.porEstado
          .map((linha) => `${linha.quantidade} ${ROTULO_ESTADO_ANUNCIO[linha.estado]}`)
          .join(' · ')}
      </p>
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  detalhe,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
  tom?: Tom;
}): React.JSX.Element {
  return (
    <div className="min-w-0 rounded-2xl border border-linha bg-superficie p-3.5 shadow-card">
      <p className="truncate text-[11px] font-[850] uppercase tracking-[0.06em] text-texto-suave">
        {rotulo}
      </p>
      <p
        className={`text-2xl font-[850] ${tom === 'sucesso' ? 'text-sucesso' : 'text-marinho'}`}
      >
        {valor}
      </p>
      {detalhe ? <p className="truncate text-[11px] text-texto-suave">{detalhe}</p> : null}
    </div>
  );
}

/**
 * O que dizer quando o filtro não devolve nada.
 *
 * Três situações que a mensagem única confundia: a conta ainda não foi
 * informada, a varredura ainda não rodou, e a varredura rodou e nenhum anúncio
 * está ativo. Só a segunda pede espera.
 */
function mensagemDeVazio(
  filtro: FiltroDeAnuncio,
  parede: { espelhadoEm: string | null; resumo: { anuncios: number; foraDoAr: number } },
): string {
  if (filtro !== 'NO_AR') return 'Nenhum anúncio fora do ar.';

  if (parede.resumo.anuncios === 0) {
    return parede.espelhadoEm === null
      ? 'Nenhum anúncio espelhado ainda. Se a conta de anúncios acabou de ser informada, a primeira varredura leva até uma hora.'
      : 'A varredura rodou e não encontrou nenhum anúncio nesta conta.';
  }

  // Há cópia, e ela está em dia: o zero é da Meta, não da varredura.
  return `Nenhum anúncio no ar. A cópia está em dia, com ${parede.resumo.foraDoAr} ${
    parede.resumo.foraDoAr === 1 ? 'anúncio' : 'anúncios'
  } fora do ar — veja a quebra por estado acima.`;
}

function CartaoDeAnuncio({ anuncio }: { anuncio: AnuncioNaParede }): React.JSX.Element {
  // A imagem vem do bucket público, por endereço que o worker montou e
  // conferiu contra a lista de domínios. Conferir o https aqui é defesa em
  // profundidade: linha gravada por outro caminho não vira `src` estranho.
  const imagem = anuncio.imagemUrl?.startsWith('https://') ? anuncio.imagemUrl : null;
  const motivo = MOTIVO_FORA_DO_AR[anuncio.estado];

  return (
    // `min-w-0` porque item de grade nasce com `min-width: auto` e não encolhe
    // abaixo do próprio min-content: sem isto, um nome longo alarga a coluna e
    // a página passa a rolar de lado no celular.
    <article
      className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-linha bg-superficie"
      data-teste="anuncio"
    >
      {imagem ? (
        // `contain`, e não `cover`: a peça aparece inteira.
        //
        // Medido nas 114 do Gerenciador: a boa vem 1080x1350 (4:5), há 720x1280
        // (9:16), e onde a Meta não entrega o criativo sobra a miniatura de
        // 64x64. Num quadro quadrado com `cover`, a de 4:5 perdia 20% da
        // altura — 10% em cima, 10% embaixo —, e é justamente no topo que mora
        // a marca. O quadro é 4:5 porque é o formato da maioria: ali a peça
        // principal preenche tudo, e as outras encostam nas bordas sem perder
        // pedaço.
        <img
          src={imagem}
          alt=""
          loading="lazy"
          decoding="async"
          className="aspect-[4/5] w-full bg-marinho-suave object-contain"
        />
      ) : (
        // Proporção igual à da imagem: cartão sem peça não encurta a fileira.
        <div className="grid aspect-[4/5] w-full place-items-center bg-marinho-suave text-[11px] text-texto-suave">
          Sem imagem
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3">
        <p className="truncate text-sm font-[850] text-marinho" title={anuncio.nome}>
          {anuncio.nome}
        </p>

        <dl className="min-w-0 text-[11px] text-texto-suave">
          <dt className="font-[850] uppercase tracking-[0.06em] opacity-70">
            Campanha de anúncios
          </dt>
          <dd className="truncate" title={anuncio.campanhaDeAnunciosNome ?? undefined}>
            {anuncio.campanhaDeAnunciosNome ?? '—'}
          </dd>
          <dt className="mt-1 font-[850] uppercase tracking-[0.06em] opacity-70">
            Conjunto de anúncios
          </dt>
          <dd className="truncate" title={anuncio.conjuntoDeAnunciosNome ?? undefined}>
            {anuncio.conjuntoDeAnunciosNome ?? '—'}
          </dd>
        </dl>

        <div className="mt-auto flex flex-col gap-1.5 border-t border-linha pt-2">
          <Pilula tom={TOM_DO_ESTADO[anuncio.estado]}>
            {ROTULO_ESTADO_ANUNCIO[anuncio.estado]}
          </Pilula>
          {motivo ? <p className="text-[11px] text-texto-suave">{motivo}</p> : null}

          {/* Os três juntos, porque a pergunta é uma só: quanto custou, quanto
              rendeu, e quanto saiu cada Lead. Separados, viram trivia. */}
          <dl className="grid grid-cols-3 gap-1 text-[11px]" data-teste="numeros-do-anuncio">
            <div className="min-w-0">
              <dt className="truncate text-texto-suave">Gasto</dt>
              <dd className="truncate font-[850] text-marinho" data-teste="gasto-do-anuncio">
                {reais(anuncio.gasto)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="truncate text-texto-suave">Leads</dt>
              {/* Tracinho, nunca zero: a origem do Lead só passou a ser
                  gravada agora, e "0" leria como "este anúncio não rendeu". */}
              <dd className="truncate font-[850] text-marinho" data-teste="leads-do-anuncio">
                {anuncio.leads ?? '—'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="truncate text-texto-suave">Por Lead</dt>
              <dd className="truncate font-[850] text-marinho" data-teste="custo-por-lead">
                {reais(anuncio.custoPorLead)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </article>
  );
}
